import { useMutation } from '@tanstack/react-query';

import { useNotification } from '@sine/sine-ui-components';

import parseServerError from '../../../helpers/parseServerError';
import { MutationKeys } from '../../../query.client';
import visitService from '../../../services/visit/visit.service';
import type { PrePass } from '../components/visitorCheckInModal.types';

interface SignInParams extends PrePass {
  invitationId?: string;
}

export function useSignInVisitor() {
  let shouldCloseModal = true;

  const { addNotification } = useNotification();

  const { mutateAsync: signIn, isLoading: isLoadingSignIn } = useMutation({
    mutationKey: [MutationKeys.IDENTITY_CHECK],
    mutationFn: async (signInValues: SignInParams) => {
      const { siteId, hostId, formResponses, identityToken, zoneId, invitationId } = signInValues;

      const createdPass = invitationId
        ? await visitService.signInInvitation(invitationId, {
            siteId,
            hostId,
            zoneId,
            formResponses,
            identityToken,
          })
        : await visitService.signIn({
            ...signInValues,
            // TODO: signinMethod is optional in the API since it detaults to 'default' value, but the TS type requires it
            // SignInVisitorRequest needs to be updated in the core api types package
            signinMethod: 'default',
          });

      return createdPass;
    },
    onSuccess: (createdPass) => {
      switch (createdPass.status) {
        case 'active':
          addNotification({
            message: 'A pass has been created',
            variant: 'success',
          });
          break;
        case 'pending':
          addNotification({
            message: 'A pass is awaiting host approval',
            variant: 'success',
          });
          break;
        default:
          // eslint-disable-next-line no-console
          console.error('Unhandled pass status within onSuccess:', createdPass.status);
      }
    },
    onError: (error: any) => {
      let errorMsg = '';
      let errorHeading = '';

      switch (error?.code) {
        case 'INTEGRATION_REQUEST_REJECTED':
          errorMsg = error?.message || 'This pass has been rejected';
          break;

        case 'INTEGRATION_REQUEST_FAILED':
          errorMsg =
            error?.message ||
            'An integration configured for this site has failed. Please try again later or contact support';
          break;

        case 'INTEGRATION_REQUEST_FAILED_UPSTREAM':
          errorMsg =
            'An integration configured for this site has failed. The upstream provider is not accessible. Please try again later or contact support.';
          break;

        case 'SITE_NOT_FOUND':
        case 'HOST_NOT_FOUND':
        case 'ZONE_NOT_FOUND':
        case 'ZONE_HOST_GROUP_MAPPING_NOT_FOUND': {
          errorHeading = `Sorry, we couldn't sign this person in!`;
          errorMsg = parseServerError(error);
          shouldCloseModal = false;
          break;
        }
        default: {
          errorHeading = `Sorry, we couldn't sign this person in!`;
          errorMsg = parseServerError(error);
        }
      }

      addNotification({
        heading: errorHeading,
        message: errorMsg,
        variant: 'danger',
      });
    },
  });

  return {
    signIn,
    shouldCloseModal,
    isLoadingSignIn,
  };
}
